// src/components/auth.jsx
// All authentication-related UI: login, set password, change password, user management.

import { useState } from "react";
import * as HS from "../hubspot.js";
import { hashPw, genTempPw, sSet, serverSave } from "../helpers.js";
import { COMPANY_BUCKETS, DEFAULT_ALLOWED_TYPES, SALES_PIPELINES, DEFAULT_ALLOWED_PIPELINES } from "../companyBuckets.js";
import { Btn, FI, Modal, Spin, Toggle } from "./ui.jsx";

export function LoginScreen({users, onLogin}) {
  const [email, setEmail]   = useState("");
  const [pw,    setPw]      = useState("");
  const [err,   setErr]     = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setErr(""); setLoading(true);
    const local = users.find(u => u.email.toLowerCase() === email.trim().toLowerCase());
    if (local) {
      if (local.passwordHash !== hashPw(pw)) {
        setErr("Incorrect password.");
        setLoading(false);
        return;
      }
      onLogin(local);
      setLoading(false);
      return;
    }
    try {
      const owners = await HS.getOwners();
      const hsUser = owners.find(o => o.email.toLowerCase() === email.trim().toLowerCase());
      if (hsUser) {
        onLogin({
          id: "hs_" + hsUser.id,
          email: hsUser.email,
          name: hsUser.name,
          passwordHash: hashPw(pw),
          isTemp: false,
          isAdmin: false,
          seeAll: true,
          allowedTypes: DEFAULT_ALLOWED_TYPES,
          allowedPipelines: DEFAULT_ALLOWED_PIPELINES,
          fromHubSpot: true,
          hubspotOwnerId: hsUser.id,
        });
        setLoading(false);
        return;
      }
    } catch (e) { /* HubSpot lookup failed - fall through */ }
    setErr("No account found. Use your HubSpot email or admin credentials.");
    setLoading(false);
  };

  return (
    <div style={{minHeight:"100vh",background:"var(--cream)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{width:"100%",maxWidth:400,animation:"fadeUp .4s ease both"}}>
        <div style={{textAlign:"center",marginBottom:36}}>
          <div style={{width:52,height:52,borderRadius:14,background:"linear-gradient(135deg,#b01020,#111111)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,margin:"0 auto 14px"}}>🛏</div>
          <h1 style={{fontSize:28,color:"var(--dark)",marginBottom:6,fontFamily:"Barlow Condensed,sans-serif",letterSpacing:1}}>ABM CRM</h1>
          <p style={{fontSize:13,color:"var(--muted)"}}>Sign in with your HubSpot or ABM admin credentials</p>
        </div>
        <div style={{background:"#fff",borderRadius:20,padding:32,boxShadow:"var(--sh-lg)",border:"1px solid var(--warm2)",display:"flex",flexDirection:"column",gap:16}}>
          <FI label="Email / Username" value={email} onChange={setEmail} placeholder="you@company.com" />
          <FI label="Password" value={pw} onChange={setPw} placeholder="Enter your password" type="password" />
          {err && <div style={{padding:"10px 14px",borderRadius:10,background:"#fdecea",color:"var(--red)",fontSize:13,fontWeight:500}}>{err}</div>}
          <Btn onClick={submit} disabled={loading} style={{width:"100%",textAlign:"center"}}>
            {loading ? <><Spin /> Signing in...</> : "Sign In"}
          </Btn>
          <p style={{fontSize:11,color:"var(--muted)",textAlign:"center",lineHeight:1.6}}>
            Use your HubSpot email &amp; password, or admin credentials.<br/>
            Forgot your password? Contact your admin.
          </p>
        </div>
      </div>
    </div>
  );
}

export function SetPasswordScreen({user, onChanged}) {
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [err, setErr] = useState("");

  const go = () => {
    setErr("");
    if (p1.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (p1 !== p2)     { setErr("Passwords do not match."); return; }
    onChanged(hashPw(p1));
  };

  return (
    <div style={{minHeight:"100vh",background:"var(--cream)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{width:"100%",maxWidth:400,animation:"fadeUp .4s ease both"}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{width:52,height:52,borderRadius:14,background:"linear-gradient(135deg,#b01020,#111111)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,margin:"0 auto 14px"}}>🔐</div>
          <h1 style={{fontSize:24,color:"var(--dark)",marginBottom:6}}>Set Your Password</h1>
          <p style={{fontSize:13,color:"var(--muted)",lineHeight:1.6}}>Welcome! Please set a permanent password to continue.</p>
        </div>
        <div style={{background:"#fff",borderRadius:20,padding:32,boxShadow:"var(--sh-lg)",border:"1px solid var(--warm2)",display:"flex",flexDirection:"column",gap:16}}>
          <FI label="New Password" value={p1} onChange={setP1} placeholder="At least 8 characters" type="password" />
          <FI label="Confirm Password" value={p2} onChange={setP2} placeholder="Repeat your password" type="password" />
          {err && <div style={{padding:"10px 14px",borderRadius:10,background:"#fdecea",color:"var(--red)",fontSize:13}}>{err}</div>}
          <Btn onClick={go} style={{width:"100%",textAlign:"center"}}>Set Password &amp; Continue</Btn>
        </div>
      </div>
    </div>
  );
}

export function ChangePwModal({user, onClose, onChanged}) {
  const [cur, setCur] = useState("");
  const [p1,  setP1]  = useState("");
  const [p2,  setP2]  = useState("");
  const [err, setErr] = useState("");

  const go = () => {
    setErr("");
    if (user.passwordHash !== hashPw(cur)) { setErr("Current password is incorrect."); return; }
    if (p1.length < 8)                     { setErr("Must be at least 8 characters."); return; }
    if (p1 !== p2)                         { setErr("Passwords do not match.");        return; }
    onChanged(hashPw(p1));
    onClose();
  };

  return (
    <Modal title="🔐 Change Password" onClose={onClose}>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <FI label="Current Password" value={cur} onChange={setCur} type="password" placeholder="Your current password" />
        <FI label="New Password"     value={p1}  onChange={setP1}  type="password" placeholder="At least 8 characters" />
        <FI label="Confirm New Password" value={p2} onChange={setP2} type="password" placeholder="Repeat new password" />
        {err && <div style={{padding:"10px 14px",borderRadius:10,background:"#fdecea",color:"var(--red)",fontSize:13}}>{err}</div>}
        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={go}>Update Password</Btn>
        </div>
      </div>
    </Modal>
  );
}

export function UserMgmt({users, onUsersChange, onClose}) {
  const [showAdd, setShowAdd] = useState(false);
  const [nm,      setNm]      = useState("");
  const [em,      setEm]      = useState("");
  const [hsId,    setHsId]    = useState("");
  const [addErr,    setAddErr]    = useState("");
  const [tempInfo,  setTempInfo]  = useState(null);
  const [resetInfo, setResetInfo] = useState(null);

  const addUser = () => {
    setAddErr("");
    if (!em.trim()) { setAddErr("Email is required."); return; }
    if (users.find(u => u.email.toLowerCase() === em.trim().toLowerCase())) {
      setAddErr("User already exists."); return;
    }
    const tp = genTempPw();
    const nu = {
      id: "u_" + Date.now(),
      email: em.trim().toLowerCase(),
      name: nm.trim() || "",
      passwordHash: hashPw(tp),
      isTemp: true,
      isAdmin: false,
      seeAll: false,
      allowedTypes: DEFAULT_ALLOWED_TYPES,
      allowedPipelines: DEFAULT_ALLOWED_PIPELINES,
      hubspotOwnerId: hsId.trim() || null,
    };
    const up = [...users, nu];
    sSet("crm_users", up);
    serverSave("crm_users", up).catch(e => console.warn("Could not push users:", e.message));
    onUsersChange(up);
    setTempInfo({name: nm || em, pw: tp});
    setNm(""); setEm(""); setHsId(""); setShowAdd(false);
  };

  const removeUser  = id => {
    const up = users.filter(u => u.id !== id);
    sSet("crm_users", up);
    serverSave("crm_users", up).catch(e => console.warn("Could not push users:", e.message));
    onUsersChange(up);
  };
  const resetPw     = u  => {
    const tp = genTempPw();
    const up = users.map(x => x.id === u.id ? {...x, passwordHash: hashPw(tp), isTemp: true} : x);
    sSet("crm_users", up);
    serverSave("crm_users", up).catch(e => console.warn("Could not push users:", e.message));
    onUsersChange(up);
    setResetInfo({name: u.name || u.email, pw: tp});
  };
  const toggleSeeAll = (uid, val) => {
    const up = users.map(u => u.id === uid ? {...u, seeAll: val} : u);
    sSet("crm_users", up);
    serverSave("crm_users", up).catch(e => console.warn("Could not push users:", e.message));
    onUsersChange(up);
  };
  const saveHsId = (uid, val) => {
    const up = users.map(u => u.id === uid ? {...u, hubspotOwnerId: val || null} : u);
    sSet("crm_users", up);
    serverSave("crm_users", up).catch(e => console.warn("Could not push users:", e.message));
    onUsersChange(up);
  };
  // Toggle a single bucket id ("mattress"|"industrial"|"other") in the user's
  // allowedTypes array. If they had full access and a chip gets unchecked,
  // their array shrinks. If they had a partial list and a chip gets checked,
  // it grows. Result is normalized to deduped, stable order.
  const toggleAllowedType = (uid, bucketId) => {
    const up = users.map(u => {
      if (u.id !== uid) return u;
      const current = Array.isArray(u.allowedTypes) ? u.allowedTypes : DEFAULT_ALLOWED_TYPES;
      const next = current.includes(bucketId)
        ? current.filter(b => b !== bucketId)
        : [...current, bucketId];
      // Normalize: keep the canonical bucket order so the saved value is stable
      const normalized = COMPANY_BUCKETS.map(b => b.id).filter(id => next.includes(id));
      return {...u, allowedTypes: normalized};
    });
    sSet("crm_users", up);
    serverSave("crm_users", up).catch(e => console.warn("Could not push users:", e.message));
    onUsersChange(up);
  };
  // Same pattern as toggleAllowedType but for the per-user pipeline visibility
  // list. The Pipeline tab uses this to decide which pipeline tabs to render
  // and which deals to show. Empty list = rep sees no pipeline tabs at all.
  const toggleAllowedPipeline = (uid, pipelineId) => {
    const up = users.map(u => {
      if (u.id !== uid) return u;
      const current = Array.isArray(u.allowedPipelines) ? u.allowedPipelines : DEFAULT_ALLOWED_PIPELINES;
      const next = current.includes(pipelineId)
        ? current.filter(p => p !== pipelineId)
        : [...current, pipelineId];
      // Normalize to canonical pipeline order so saved value is stable
      const normalized = SALES_PIPELINES.map(p => p.id).filter(id => next.includes(id));
      return {...u, allowedPipelines: normalized};
    });
    sSet("crm_users", up);
    serverSave("crm_users", up).catch(e => console.warn("Could not push users:", e.message));
    onUsersChange(up);
  };

  return (
    <Modal title="User Management" onClose={onClose} wide>
      {tempInfo && (
        <div style={{padding:"14px 18px",borderRadius:12,background:"#e6f4ed",border:"1px solid #b2dfcb",marginBottom:16,fontSize:13}}>
          <strong>{tempInfo.name}</strong> created. Temp password: <code style={{background:"#fff",padding:"2px 8px",borderRadius:6,fontWeight:700}}>{tempInfo.pw}</code>
          <button onClick={()=>setTempInfo(null)} style={{marginLeft:12,background:"none",border:"none",cursor:"pointer"}}>x</button>
        </div>
      )}
      {resetInfo && (
        <div style={{padding:"14px 18px",borderRadius:12,background:"#fff8e6",border:"1px solid #fde68a",marginBottom:16,fontSize:13}}>
          Reset for <strong>{resetInfo.name}</strong>: <code style={{background:"#fff",padding:"2px 8px",borderRadius:6,fontWeight:700}}>{resetInfo.pw}</code>
          <button onClick={()=>setResetInfo(null)} style={{marginLeft:12,background:"none",border:"none",cursor:"pointer"}}>x</button>
        </div>
      )}
      <div style={{marginBottom:20}}>
        {users.filter(u => !u.isAdmin).length === 0 && (
          <div style={{padding:"24px 0",textAlign:"center",color:"var(--muted)",fontSize:13,fontStyle:"italic"}}>No sales reps yet.</div>
        )}
        {users.filter(u => !u.isAdmin).map(u => (
          <div key={u.id} style={{padding:"14px 16px",borderRadius:12,background:"var(--warm1)",border:"1px solid var(--warm2)",marginBottom:8}}>
            <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
              <div style={{width:36,height:36,borderRadius:99,background:"var(--brown)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,fontWeight:700,flexShrink:0}}>
                {(u.name || u.email)[0].toUpperCase()}
              </div>
              <div style={{flex:1,minWidth:120}}>
                <div style={{fontWeight:600,fontSize:14}}>
                  {u.name || <span style={{color:"var(--muted)",fontStyle:"italic"}}>No name</span>}
                </div>
                <div style={{fontSize:12,color:"var(--muted)"}}>{u.email}</div>
              </div>
              <Toggle checked={u.seeAll || false} onChange={v => toggleSeeAll(u.id, v)} label={<span style={{fontSize:11}}>See all accounts</span>} />
              <div style={{display:"flex",gap:6}}>
                <button onClick={()=>resetPw(u)} style={{padding:"5px 12px",borderRadius:99,border:"1px solid var(--warm2)",background:"#fff",fontSize:12,cursor:"pointer",fontFamily:"Barlow,sans-serif"}}>Reset PW</button>
                <button onClick={()=>removeUser(u.id)} style={{padding:"5px 12px",borderRadius:99,border:"1px solid #f5c6c6",background:"#fdecea",color:"var(--red)",fontSize:12,cursor:"pointer",fontFamily:"Barlow,sans-serif"}}>Remove</button>
              </div>
            </div>
            {/* Allowed company-type buckets. Defaults to all 3 = full access.
                Tap a chip to toggle that bucket on/off for this rep. */}
            <div style={{display:"flex",alignItems:"center",gap:8,marginTop:10,paddingTop:8,borderTop:"1px solid var(--warm2)",flexWrap:"wrap"}}>
              <span style={{fontSize:11,color:"var(--muted)",whiteSpace:"nowrap"}}>Can see:</span>
              {COMPANY_BUCKETS.map(b => {
                const allowed = (Array.isArray(u.allowedTypes) ? u.allowedTypes : DEFAULT_ALLOWED_TYPES).includes(b.id);
                return (
                  <button key={b.id} onClick={() => toggleAllowedType(u.id, b.id)}
                    style={{
                      padding:"4px 12px",borderRadius:99,fontSize:11,fontWeight:600,cursor:"pointer",
                      border:"1.5px solid " + (allowed ? "var(--brown)" : "var(--warm2)"),
                      background: allowed ? "var(--brown)" : "#fff",
                      color: allowed ? "#fff" : "var(--muted)",
                      fontFamily:"Barlow,sans-serif",transition:"all .15s",
                    }}
                    title={allowed ? "Click to remove " + b.label + " access" : "Click to grant " + b.label + " access"}>
                    {allowed ? "✓ " : ""}{b.label}
                  </button>
                );
              })}
              {!(Array.isArray(u.allowedTypes) ? u.allowedTypes : DEFAULT_ALLOWED_TYPES).length && (
                <span style={{fontSize:11,color:"var(--red)",fontWeight:600,marginLeft:6}}>No access — rep will see no companies</span>
              )}
            </div>
            {/* Allowed pipelines. Defaults to ["mattress","abm"] for new reps.
                These chips gate which pipeline tabs the rep sees on the
                Pipeline screen, independently of the company-type chips
                above. Wrap is enabled because there are 7 chips. */}
            <div style={{display:"flex",alignItems:"center",gap:6,marginTop:10,paddingTop:8,borderTop:"1px solid var(--warm2)",flexWrap:"wrap"}}>
              <span style={{fontSize:11,color:"var(--muted)",whiteSpace:"nowrap",marginRight:2}}>Pipelines:</span>
              {SALES_PIPELINES.map(p => {
                const allowed = (Array.isArray(u.allowedPipelines) ? u.allowedPipelines : DEFAULT_ALLOWED_PIPELINES).includes(p.id);
                return (
                  <button key={p.id} onClick={() => toggleAllowedPipeline(u.id, p.id)}
                    style={{
                      padding:"4px 10px",borderRadius:99,fontSize:11,fontWeight:600,cursor:"pointer",
                      border:"1.5px solid " + (allowed ? "var(--brown)" : "var(--warm2)"),
                      background: allowed ? "var(--brown)" : "#fff",
                      color: allowed ? "#fff" : "var(--muted)",
                      fontFamily:"Barlow,sans-serif",transition:"all .15s",
                    }}
                    title={allowed ? "Click to remove " + p.label + " pipeline access" : "Click to grant " + p.label + " pipeline access"}>
                    {allowed ? "✓ " : ""}{p.label}
                  </button>
                );
              })}
              {!(Array.isArray(u.allowedPipelines) ? u.allowedPipelines : DEFAULT_ALLOWED_PIPELINES).length && (
                <span style={{fontSize:11,color:"var(--red)",fontWeight:600,marginLeft:6}}>No pipelines — rep will see empty Pipeline tab</span>
              )}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8,marginTop:10,paddingTop:8,borderTop:"1px solid var(--warm2)"}}>
              <span style={{fontSize:11,color:"var(--muted)",whiteSpace:"nowrap"}}>HubSpot Owner ID:</span>
              <input
                defaultValue={u.hubspotOwnerId || ""}
                placeholder="e.g. 12345678"
                onBlur={e => saveHsId(u.id, e.target.value.trim())}
                style={{fontSize:12,padding:"4px 10px",borderRadius:8,border:"1px solid var(--warm2)",background:"#fff",flex:1,maxWidth:180,outline:"none"}}
              />
              {u.hubspotOwnerId
                ? <span style={{fontSize:11,color:"var(--green)",fontWeight:600}}>linked to HubSpot</span>
                : <span style={{fontSize:11,color:"var(--muted)"}}>enter to enable Progress tracking</span>
              }
            </div>
          </div>
        ))}
      </div>
      {!showAdd
        ? <Btn variant="soft" onClick={()=>setShowAdd(true)}>+ Add User</Btn>
        : (
          <div style={{padding:20,borderRadius:14,border:"1.5px dashed var(--warm3)",background:"var(--warm1)",display:"flex",flexDirection:"column",gap:12}}>
            <div style={{fontSize:14,fontWeight:600,color:"var(--dark)"}}>New User</div>
            <FI label="Full Name (optional)" value={nm} onChange={setNm} placeholder="e.g. Jane Smith" />
            <FI label="Email" value={em} onChange={setEm} placeholder="jane@abminternational.com" />
            <FI label="HubSpot Owner ID (optional)" value={hsId} onChange={setHsId} placeholder="e.g. 12345678" />
            <div style={{fontSize:11,color:"var(--muted)",lineHeight:1.5}}>
              Find the HubSpot Owner ID in HubSpot Settings &gt; Users &amp; Teams &gt; click user &gt; copy the number from the URL.
            </div>
            {addErr && <div style={{padding:"8px 12px",borderRadius:8,background:"#fdecea",color:"var(--red)",fontSize:13}}>{addErr}</div>}
            <div style={{display:"flex",gap:8}}>
              <Btn onClick={addUser}>Create User</Btn>
              <Btn variant="ghost" onClick={()=>{setShowAdd(false);setNm("");setEm("");setHsId("");setAddErr("");}}>Cancel</Btn>
            </div>
          </div>
        )
      }
    </Modal>
  );
}
